import { berlinDayKey } from "./calendarDays.js";
import type { CalendarEntry, EventRunView } from "./types.js";

export type EventDisplayState = "claimable" | "active" | "upcoming" | "ended";

export function eventDestination(run: Pick<CalendarEntry, "category" | "runId">): string {
  return run.category === "daily" ? "/app/events/daily" : `/app/events/plaza/${encodeURIComponent(run.runId)}`;
}

/** History paths preserve percent escapes; persisted run IDs contain @ separators. */
export function eventRunIdFromRoute(route: string): string | undefined {
  const segment = route.split("/")[4];
  if (!segment) return undefined;
  try { return decodeURIComponent(segment); } catch { return undefined; }
}

/** Claimability is supplied by the server; time labels follow persisted run boundaries. */
export function eventDisplayState(run: Pick<CalendarEntry, "startsAt" | "endsAt">, now: number, claimable = false): EventDisplayState {
  if (claimable) return "claimable";
  if (run.startsAt > now) return "upcoming";
  return run.endsAt <= now ? "ended" : "active";
}

export function orderedEventRuns(runs: EventRunView[], now: number): EventRunView[] {
  const priority = { claimable: 0, active: 1, upcoming: 2, ended: 3 };
  return [...runs].sort((a, b) => {
    const stateA = eventDisplayState(a, now, a.milestones.some(m => m.claimable));
    const stateB = eventDisplayState(b, now, b.milestones.some(m => m.claimable));
    return priority[stateA] - priority[stateB] || (stateA === "upcoming" ? a.startsAt - b.startsAt : a.endsAt - b.endsAt) || a.runId.localeCompare(b.runId);
  });
}

export function calendarAgenda(calendar: CalendarEntry[], selectedDay: string, now: number): CalendarEntry[] {
  return [...new Map(calendar.filter(run => berlinDayKey(run.startsAt) <= selectedDay && berlinDayKey(run.endsAt - 1) >= selectedDay).map(run => [run.runId, run])).values()]
    .sort((a, b) => {
      const priority = { active: 0, upcoming: 1, ended: 2, claimable: 0 };
      const stateA = eventDisplayState(a, now), stateB = eventDisplayState(b, now);
      return priority[stateA] - priority[stateB] || (stateA === "upcoming" ? a.startsAt - b.startsAt : a.endsAt - b.endsAt) || a.runId.localeCompare(b.runId);
    });
}
